import { useState, useCallback, useId } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  GripVertical,
  Plus,
  Trash2,
  Edit,
  Copy,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  Lock,
  Columns,
  LayoutGrid,
  Table2,
  Upload,
  ArrowLeft,
  Type,
  AlignLeft,
  Hash,
  DollarSign,
  Phone,
  Mail,
  Link,
  KeyRound,
  List,
  Calendar,
  CheckSquare,
  FileText,
  Check,
  Eye,
  EyeOff,
  GitBranch,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { getIcon } from "@/lib/brandIcons";
import type { GenericCategory, GenericSection, GenericField } from "@shared/schema";

// ── Field types ───────────────────────────────────────────────────────────────

const FIELD_TYPES = [
  { value: "text",      label: "Text",      icon: Type,        description: "Single line text" },
  { value: "multiline", label: "Multiline", icon: AlignLeft,   description: "Multi-line text area" },
  { value: "number",    label: "Number",    icon: Hash,        description: "Numeric input" },
  { value: "currency",  label: "Currency",  icon: DollarSign,  description: "Monetary amount" },
  { value: "phone",     label: "Phone",     icon: Phone,       description: "Phone number" },
  { value: "email",     label: "Email",     icon: Mail,        description: "Email address" },
  { value: "url",       label: "URL",       icon: Link,        description: "Web address" },
  { value: "password",  label: "Password",  icon: Lock,        description: "Masked password" },
  { value: "pin",       label: "PIN",       icon: KeyRound,    description: "Short numeric code" },
  { value: "select",    label: "Dropdown",  icon: List,        description: "Select from options" },
  { value: "date",      label: "Date",      icon: Calendar,    description: "Date picker" },
  { value: "checkbox",  label: "Checkbox",  icon: CheckSquare, description: "Yes/No toggle" },
  { value: "file_ref",  label: "File",      icon: FileText,    description: "Document attachment" },
  { value: "notes",     label: "Notes",     icon: AlignLeft,   description: "Notes field" },
] as const;

type FieldType = typeof FIELD_TYPES[number]["value"];

type ConditionOperator = "equals" | "not_equals" | "contains" | "is_empty" | "is_not_empty";

interface FieldConditions {
  showIf: { fieldId: string; operator: ConditionOperator; value: string };
}

interface FieldFormData {
  name: string;
  fieldKey: string;
  fieldType: FieldType;
  placeholder: string;
  icon: string;
  options: { choices?: string[]; min?: number; max?: number } | null;
  isRequired: boolean;
  isCritical: boolean;
  isEncrypted: boolean;
  columnWidth: number | null;
  fieldGroup: string;
  extractionKey: string;
  instructions: string;
  conditions: FieldConditions | null;
}

const defaultFieldForm: FieldFormData = {
  name: "",
  fieldKey: "",
  fieldType: "text",
  placeholder: "",
  icon: "FileText",
  options: null,
  isRequired: false,
  isCritical: false,
  isEncrypted: false,
  columnWidth: null,
  fieldGroup: "",
  extractionKey: "",
  instructions: "",
  conditions: null,
};

function generateFieldKey(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

// ── FieldDialog ───────────────────────────────────────────────────────────────

interface FieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  form: FieldFormData;
  setForm: (form: FieldFormData | ((prev: FieldFormData) => FieldFormData)) => void;
  onNameChange: (name: string) => void;
  onSubmit: () => void;
  isPending: boolean;
  submitLabel: string;
  sectionFields: GenericField[];
}

function FieldDialog({
  open, onOpenChange, title, description,
  form, setForm, onNameChange, onSubmit, isPending, submitLabel, sectionFields,
}: FieldDialogProps) {
  const uid = useId();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Name + Key */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="field-name">Field Name *</Label>
              <Input
                id="field-name"
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g., Account Number"
                data-testid="input-field-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="field-key">Field Key *</Label>
              <Input
                id="field-key"
                value={form.fieldKey}
                onChange={(e) => setForm(prev => ({ ...prev, fieldKey: e.target.value }))}
                placeholder="e.g., account_number"
                data-testid="input-field-key"
              />
              <p className="text-xs text-muted-foreground">Auto-generated from name</p>
            </div>
          </div>

          {/* Field Type grid */}
          <div className="space-y-2">
            <Label>Field Type *</Label>
            <div className="grid grid-cols-3 gap-2">
              {FIELD_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = form.fieldType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, fieldType: type.value }))}
                    className={`flex items-center gap-2 p-3 border rounded-lg text-left transition-colors ${
                      isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                    }`}
                    data-testid={`button-field-type-${type.value}`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    <div>
                      <div className={`text-sm font-medium ${isSelected ? "text-primary" : ""}`}>{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dropdown choices */}
          {form.fieldType === "select" && (
            <div className="space-y-2">
              <Label htmlFor={`${uid}-dropdown-options`}>Dropdown Options</Label>
              <Textarea id={`${uid}-dropdown-options`}
                value={form.options?.choices?.join("\n") || ""}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  options: { ...prev.options, choices: e.target.value.split("\n").filter(c => c.trim()) },
                }))}
                placeholder="Enter one option per line"
                rows={4}
                data-testid="input-dropdown-options"
              />
              <p className="text-xs text-muted-foreground">One option per line</p>
            </div>
          )}

          {/* Placeholder */}
          <div className="space-y-2">
            <Label htmlFor={`${uid}-placeholder-text`}>Placeholder Text</Label>
            <Input id={`${uid}-placeholder-text`}
              value={form.placeholder}
              onChange={(e) => setForm(prev => ({ ...prev, placeholder: e.target.value }))}
              placeholder="e.g., Enter account number..."
              data-testid="input-placeholder"
            />
          </div>

          {/* Instructions */}
          <div className="space-y-2">
            <Label htmlFor={`${uid}-help-text`}>Help Text / Instructions</Label>
            <Textarea id={`${uid}-help-text`}
              value={form.instructions}
              onChange={(e) => setForm(prev => ({ ...prev, instructions: e.target.value }))}
              placeholder="Guidance shown to users as a tooltip"
              rows={3}
              data-testid="input-instructions"
            />
          </div>

          {/* Field Group + Column Width */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${uid}-field-group`}>Field Group</Label>
              <Input id={`${uid}-field-group`}
                value={form.fieldGroup}
                onChange={(e) => setForm(prev => ({ ...prev, fieldGroup: e.target.value }))}
                placeholder="e.g., Contact Information"
                data-testid="input-field-group"
              />
              <p className="text-xs text-muted-foreground">Optional grouping label within the section</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${uid}-column-width`}>Column Width (px)</Label>
              <Input id={`${uid}-column-width`}
                type="number"
                value={form.columnWidth ?? ""}
                onChange={(e) => setForm(prev => ({ ...prev, columnWidth: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder="e.g., 200"
                data-testid="input-column-width"
              />
              <p className="text-xs text-muted-foreground">For table layout columns</p>
            </div>
          </div>

          {/* Extraction Key */}
          <div className="space-y-2">
            <Label htmlFor={`${uid}-extraction-key`}>GenAI Extraction Key</Label>
            <Input id={`${uid}-extraction-key`}
              value={form.extractionKey}
              onChange={(e) => setForm(prev => ({ ...prev, extractionKey: e.target.value }))}
              placeholder="e.g., bank_account_number"
              data-testid="input-extraction-key"
            />
            <p className="text-xs text-muted-foreground">Key for auto-populating from document extraction</p>
          </div>

          {/* Conditional Visibility */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base" htmlFor={`${uid}-conditional-visibility`}>Conditional Visibility</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Show this field only when another field meets a condition</p>
              </div>
              <Switch id={`${uid}-conditional-visibility`}
                checked={!!form.conditions}
                onCheckedChange={(checked) => setForm(prev => ({
                  ...prev,
                  conditions: checked ? { showIf: { fieldId: "", operator: "equals", value: "" } } : null,
                }))}
              />
            </div>
            {form.conditions && (
              <div className="space-y-2 pt-1">
                <p className="text-xs text-muted-foreground">Show this field when:</p>
                <div className="grid grid-cols-3 gap-2">
                  <Select
                    value={form.conditions.showIf.fieldId}
                    onValueChange={(v) => setForm(prev => ({
                      ...prev,
                      conditions: { ...prev.conditions!, showIf: { ...prev.conditions!.showIf, fieldId: v } },
                    }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select field…" />
                    </SelectTrigger>
                    <SelectContent>
                      {sectionFields.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={form.conditions.showIf.operator}
                    onValueChange={(v) => setForm(prev => ({
                      ...prev,
                      conditions: { ...prev.conditions!, showIf: { ...prev.conditions!.showIf, operator: v as ConditionOperator } },
                    }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">equals</SelectItem>
                      <SelectItem value="not_equals">does not equal</SelectItem>
                      <SelectItem value="contains">contains</SelectItem>
                      <SelectItem value="is_empty">is empty</SelectItem>
                      <SelectItem value="is_not_empty">is not empty</SelectItem>
                    </SelectContent>
                  </Select>
                  {!["is_empty", "is_not_empty"].includes(form.conditions.showIf.operator) && (
                    <input
                      className="h-8 px-3 text-xs border rounded-md bg-background"
                      value={form.conditions.showIf.value}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        conditions: { ...prev.conditions!, showIf: { ...prev.conditions!.showIf, value: e.target.value } },
                      }))}
                      placeholder="Value…"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Flags */}
          <div className="border rounded-lg p-4 space-y-3">
            <Label className="text-base">Field Flags</Label>
            {[
              { key: "isRequired",  label: "Required field" },
              { key: "isCritical",  label: "Critical (highlighted for attention)" },
              { key: "isEncrypted", label: "Encrypt at rest (passwords, PINs, sensitive data)" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2">
                <Checkbox
                  id={`flag-${key}`}
                  checked={form[key as keyof FieldFormData] as boolean}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, [key]: !!checked }))}
                  data-testid={`checkbox-${key}`}
                />
                <Label htmlFor={`flag-${key}`} className="text-sm font-normal">{label}</Label>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={!form.name.trim() || isPending} data-testid="button-submit-field">
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── SortableFieldRow ──────────────────────────────────────────────────────────

interface SortableFieldRowProps {
  field: GenericField;
  onEdit: (field: GenericField) => void;
  onDuplicate: (fieldId: string) => void;
  isDuplicating: boolean;
  isTable: boolean;
}

function SortableFieldRow({ field, onEdit, onDuplicate, isDuplicating, isTable }: SortableFieldRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const typeInfo = FIELD_TYPES.find(t => t.value === field.fieldType);
  const TypeIcon = typeInfo?.icon || Type;

  const colSpanClass = "col-span-12";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${colSpanClass} group`}
      data-testid={`field-row-${field.id}`}
    >
      <div className="h-full border rounded-lg bg-background hover:border-primary/50 hover:shadow-sm transition-all p-3 flex flex-col gap-2">
        {/* Field header */}
        <div className="flex items-start gap-2">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing shrink-0 mt-0.5">
            <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <TypeIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="font-medium text-sm truncate">{field.name}</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap mt-1">
              <Badge variant="secondary" className="text-xs px-1.5 py-0">{typeInfo?.label || field.fieldType}</Badge>
              {field.conditions && (
                <Badge variant="outline" className="text-xs px-1.5 py-0 gap-1">
                  <GitBranch className="h-2.5 w-2.5" />Conditional
                </Badge>
              )}
              {field.isRequired  && <Badge variant="outline" className="text-xs px-1.5 py-0">Required</Badge>}
              {field.isCritical  && <Badge variant="destructive" className="text-xs px-1.5 py-0">Critical</Badge>}
              {field.isEncrypted && <Badge variant="outline" className="text-xs px-1.5 py-0">Encrypted</Badge>}
            </div>
            {field.placeholder && <p className="text-xs text-muted-foreground mt-1 italic truncate">{field.placeholder}</p>}
          </div>
        </div>

        {/* Field mock input */}
        {field.fieldType === "multiline" || field.fieldType === "notes" ? (
          <div className="h-10 rounded border border-dashed border-muted-foreground/30 bg-muted/20 px-2 py-1 flex items-start">
            <span className="text-xs text-muted-foreground/50 truncate">{field.placeholder || "Multi-line text…"}</span>
          </div>
        ) : field.fieldType === "checkbox" ? (
          <div className="flex items-center gap-2 py-1">
            <div className="h-3.5 w-3.5 rounded border border-dashed border-muted-foreground/40 shrink-0" />
            <span className="text-xs text-muted-foreground/50">{field.placeholder || "Yes / No"}</span>
          </div>
        ) : (
          <div className="h-7 rounded border border-dashed border-muted-foreground/30 bg-muted/20 px-2 flex items-center">
            <span className="text-xs text-muted-foreground/50 truncate">{field.placeholder || field.name}</span>
          </div>
        )}

        {/* Controls row */}
        <div className="flex items-center gap-1 pt-0.5">
          <div className="flex-1" />
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button aria-label="Edit field" size="icon" variant="ghost" className="h-6 w-6" onClick={() => onEdit(field)} data-testid={`button-edit-field-${field.id}`}>
              <Edit className="h-3 w-3" />
            </Button>
            <Button aria-label="Duplicate field" size="icon" variant="ghost" className="h-6 w-6" onClick={() => onDuplicate(field.id)} disabled={isDuplicating} data-testid={`button-duplicate-field-${field.id}`}>
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SectionCard ───────────────────────────────────────────────────────────────

interface SectionCardProps {
  section: GenericSection;
  categorySlug: string;
  onDelete: (section: GenericSection) => void;
  onDuplicated: () => void;
  dragHandleProps: Record<string, unknown>;
  dragRef: (node: HTMLElement | null) => void;
  dragStyle: React.CSSProperties;
}

function SectionCard({ section, categorySlug, onDelete, onDuplicated, dragHandleProps, dragRef, dragStyle }: SectionCardProps) {
  const uid = useId();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(section.name);

  // Fields query (lazy — only fires when section is open)
  const { data: fields = [], isLoading: fieldsLoading } = useQuery<GenericField[]>({
    queryKey: ["/api/admin/field-config/sections", section.id, "fields"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/field-config/sections/${section.id}/fields`);
      if (!res.ok) throw new Error("Failed to load fields");
      return res.json();
    },
    enabled: isOpen,
  });

  const sortedFields = [...fields].filter(f => f.isActive !== false).sort((a, b) => a.sortOrder - b.sortOrder);

  // Section mutations
  const updateSectionMutation = useMutation({
    mutationFn: async (updates: Partial<GenericSection>) => {
      const res = await apiRequest("PATCH", `/api/admin/field-config/sections/${section.id}`, updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories", section.categoryId, "sections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/field-config/categories"] });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const renameSectionMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("PATCH", `/api/admin/field-config/sections/${section.id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories", section.categoryId, "sections"] });
      setIsEditingName(false);
      toast({ title: "Section renamed" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170). Admin surfaces are single-operator and lower blast radius,
  // but every one of these mints a row per fire — a duplicated section, field, or template row is
  // real template state the reconciler will then converge everywhere.
  const dupSectionFlight = useSingleFlight();
  const createFieldFlight = useSingleFlight();
  const dupFieldFlight = useSingleFlight();

  const duplicateSectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/admin/field-config/sections/${section.id}/duplicate`);
      return res.json();
    },
    onSuccess: () => {
      onDuplicated();
      toast({ title: "Section duplicated", description: "A copy has been added below." });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // Field mutations
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [editFieldOpen, setEditFieldOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<GenericField | null>(null);
  const [fieldForm, setFieldForm] = useState<FieldFormData>(defaultFieldForm);

  const createFieldMutation = useMutation({
    mutationFn: async (data: FieldFormData) => {
      const res = await apiRequest("POST", `/api/admin/field-config/sections/${section.id}/fields`, {
        ...data,
        options: data.options || undefined,
        columnWidth: data.columnWidth || undefined,
        fieldGroup: data.fieldGroup || undefined,
        extractionKey: data.extractionKey || undefined,
        instructions: data.instructions || undefined,
        conditions: data.conditions,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/field-config/sections", section.id, "fields"] });
      queryClient.invalidateQueries({ queryKey: ["/api/generic-fields/by-type"] });
      toast({ title: "Field created" });
      setAddFieldOpen(false);
      setFieldForm(defaultFieldForm);
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateFieldMutation = useMutation({
    mutationFn: async ({ fieldId, data }: { fieldId: string; data: Partial<FieldFormData> }) => {
      const res = await apiRequest("PATCH", `/api/admin/field-config/fields/${fieldId}`, {
        ...data,
        options: data.options || undefined,
        columnWidth: data.columnWidth || undefined,
        fieldGroup: data.fieldGroup || undefined,
        extractionKey: data.extractionKey || undefined,
        instructions: data.instructions || undefined,
        conditions: data.conditions,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/field-config/sections", section.id, "fields"] });
      queryClient.invalidateQueries({ queryKey: ["/api/generic-fields/by-type"] });
      toast({ title: "Field updated" });
      setEditFieldOpen(false);
      setSelectedField(null);
      setFieldForm(defaultFieldForm);
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // s220 Lane G: deleteFieldMutation removed. A template field's home is `shared/template-spec/`;
  // deleting the row cascaded away every client's cell and the reconciler re-minted the field on
  // the next boot. Retire a field in the spec (isActive: false) instead.

  const duplicateFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      const res = await apiRequest("POST", `/api/admin/field-config/fields/${fieldId}/duplicate`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/field-config/sections", section.id, "fields"] });
      toast({ title: "Field duplicated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const reorderFieldsMutation = useMutation({
    mutationFn: async (fieldIds: string[]) => {
      await apiRequest("POST", "/api/admin/field-config/fields/reorder", { sectionId: section.id, fieldIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/field-config/sections", section.id, "fields"] });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const fieldSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleFieldDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sortedFields.findIndex(f => f.id === active.id);
    const newIdx = sortedFields.findIndex(f => f.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      reorderFieldsMutation.mutate(arrayMove(sortedFields, oldIdx, newIdx).map(f => f.id));
    }
  };

  const handleEditField = useCallback((field: GenericField) => {
    setSelectedField(field);
    setFieldForm({
      name: field.name,
      fieldKey: field.fieldKey,
      fieldType: field.fieldType as FieldType,
      placeholder: field.placeholder || "",
      icon: field.icon || "FileText",
      options: field.options as FieldFormData["options"],
      isRequired: field.isRequired || false,
      isCritical: field.isCritical || false,
      isEncrypted: field.isEncrypted || false,
      columnWidth: field.columnWidth,
      fieldGroup: field.fieldGroup || "",
      extractionKey: field.extractionKey || "",
      instructions: field.instructions || "",
      conditions: (field.conditions as FieldConditions | null) ?? null,
    });
    setEditFieldOpen(true);
  }, []);

  const isTable = section.layoutType === "table";

  return (
    <div ref={dragRef} style={dragStyle} className="border rounded-lg bg-card shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Section header */}
        <div className="flex items-center gap-2 p-3">
          <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing shrink-0">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>

          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 flex-1 text-left min-w-0">
              {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
              {isEditingName ? (
                <Input
                  autoFocus
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onBlur={() => {
                    if (nameInput.trim() && nameInput !== section.name) {
                      renameSectionMutation.mutate(nameInput.trim());
                    } else {
                      setNameInput(section.name);
                      setIsEditingName(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                    if (e.key === "Escape") { setNameInput(section.name); setIsEditingName(false); }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-7 text-sm font-medium"
                />
              ) : (
                <span className="font-medium text-sm truncate">{section.name}</span>
              )}
            </button>
          </CollapsibleTrigger>

          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant={isTable ? "default" : "secondary"} className="text-xs">
              {isTable ? <><Table2 className="h-3 w-3 mr-1" />Table</> : <><LayoutGrid className="h-3 w-3 mr-1" />Card</>}
            </Badge>
            <Badge variant="outline" className="text-xs">{fields.length} fields</Badge>

            <Button aria-label="Rename section" size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setIsEditingName(true); }} data-testid={`button-rename-section-${section.id}`}>
              <Edit className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); dupSectionFlight.runQuiet(() => duplicateSectionMutation.mutateAsync()); }} disabled={dupSectionFlight.inFlight || duplicateSectionMutation.isPending} title="Duplicate section" data-testid={`button-duplicate-section-${section.id}`}>
              {duplicateSectionMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button aria-label="Delete section" size="icon" variant="ghost" className="h-7 w-7 hover:text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(section); }} data-testid={`button-delete-section-${section.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <CollapsibleContent>
          <div className="border-t px-4 py-4 space-y-5">
            {/* Section settings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Layout type */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Layout Type</Label>
                <div className="flex gap-2">
                  <Button
                    variant={!isTable ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateSectionMutation.mutate({ layoutType: "card" })}
                    disabled={updateSectionMutation.isPending}
                    className="flex-1"
                    data-testid={`button-layout-card-${section.id}`}
                  >
                    <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />Card
                  </Button>
                  <Button
                    variant={isTable ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateSectionMutation.mutate({ layoutType: "table" })}
                    disabled={updateSectionMutation.isPending}
                    className="flex-1"
                    data-testid={`button-layout-table-${section.id}`}
                  >
                    <Table2 className="h-3.5 w-3.5 mr-1.5" />Table
                  </Button>
                </div>
                {isTable ? (
                  <p className="text-xs text-muted-foreground">
                    <strong>Table:</strong> Client can add multiple rows (e.g. list of bank accounts — one row per account, each field is a column)
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    <strong>Card:</strong> Client fills this in once (e.g. attorney details — name, phone, address each appear as a single input)
                  </p>
                )}
              </div>

              {/* Document uploads */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" htmlFor={`${uid}-document-uploads`}>Document Uploads</Label>
                <div className="flex items-center gap-3 pt-1">
                  <Switch id={`${uid}-document-uploads`}
                    checked={section.allowDocuments !== false}
                    onCheckedChange={(v) => updateSectionMutation.mutate({ allowDocuments: v })}
                    disabled={updateSectionMutation.isPending}
                    data-testid={`switch-allow-documents-${section.id}`}
                  />
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Upload className="h-3.5 w-3.5" />
                    {section.allowDocuments !== false ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Show a file upload area so clients can attach documents in this section</p>
              </div>
            </div>

            {/* Fields list */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Fields ({sortedFields.length})
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isTable
                      ? "Each field becomes a column in the table. Add as many as you need."
                      : "Each field is one input on the form. Add as many as you need, then drag to reorder."}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs shrink-0" onClick={() => { setFieldForm(defaultFieldForm); setAddFieldOpen(true); }} data-testid={`button-add-field-${section.id}`}>
                  <Plus className="h-3.5 w-3.5" />Add Field
                </Button>
              </div>

              {fieldsLoading ? (
                <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />Loading fields...
                </div>
              ) : sortedFields.length === 0 ? (
                <button
                  className="w-full text-sm text-primary py-6 text-center border rounded-lg border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors flex flex-col items-center gap-1"
                  onClick={() => { setFieldForm(defaultFieldForm); setAddFieldOpen(true); }}
                >
                  <Plus className="h-5 w-5" />
                  <span className="font-medium">Add your first field</span>
                  <span className="text-xs text-muted-foreground">e.g. Attorney Name (Text), Date Signed (Date), Signed? (Checkbox)</span>
                </button>
              ) : (
                <DndContext sensors={fieldSensors} collisionDetection={closestCenter} onDragEnd={handleFieldDragEnd}>
                  <SortableContext items={sortedFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    <div className={isTable ? "space-y-2" : "grid grid-cols-12 gap-3"}>
                      {sortedFields.map(field => (
                        <SortableFieldRow
                          key={field.id}
                          field={field}
                          onEdit={handleEditField}
                          onDuplicate={(id) => dupFieldFlight.runQuiet(() => duplicateFieldMutation.mutateAsync(id))}
                          isDuplicating={duplicateFieldMutation.isPending}
                          isTable={isTable}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              {sortedFields.length > 0 && !isTable && (
                <p className="text-xs text-muted-foreground pt-1">
                  Use <strong>1/1 · 1/2 · 1/3</strong> on each field to control how wide it appears. Hover a field for edit/duplicate/delete. Drag the grip ⠿ to reorder.
                </p>
              )}
              {sortedFields.length > 0 && isTable && (
                <p className="text-xs text-muted-foreground pt-1">
                  Each field is a column in the table. Hover a field to edit, duplicate, or delete. Drag to reorder columns.
                </p>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Dialogs */}
      <FieldDialog
        open={addFieldOpen}
        onOpenChange={setAddFieldOpen}
        title="Add Field"
        description={`Add a new field to "${section.name}"`}
        form={fieldForm}
        setForm={setFieldForm}
        onNameChange={(name) => setFieldForm(prev => ({ ...prev, name, fieldKey: prev.fieldKey === generateFieldKey(prev.name) ? generateFieldKey(name) : prev.fieldKey }))}
        onSubmit={() => createFieldFlight.runQuiet(() => createFieldMutation.mutateAsync(fieldForm))}
        isPending={createFieldMutation.isPending}
        submitLabel="Add Field"
        sectionFields={sortedFields}
      />

      <FieldDialog
        open={editFieldOpen}
        onOpenChange={setEditFieldOpen}
        title="Edit Field"
        description={`Edit "${selectedField?.name}"`}
        form={fieldForm}
        setForm={setFieldForm}
        onNameChange={(name) => setFieldForm(prev => ({ ...prev, name }))}
        onSubmit={() => selectedField && updateFieldMutation.mutate({ fieldId: selectedField.id, data: fieldForm })}
        isPending={updateFieldMutation.isPending}
        submitLabel="Save Changes"
        sectionFields={sortedFields.filter(f => f.id !== selectedField?.id)}
      />

    </div>
  );
}

// ── SortableSectionCard ───────────────────────────────────────────────────────

function SortableSectionCard({ section, categorySlug, onDelete, onDuplicated }: { section: GenericSection; categorySlug: string; onDelete: (s: GenericSection) => void; onDuplicated: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style: React.CSSProperties = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <SectionCard
      section={section}
      categorySlug={categorySlug}
      onDelete={onDelete}
      onDuplicated={onDuplicated}
      dragHandleProps={{ ...attributes, ...listeners }}
      dragRef={setNodeRef}
      dragStyle={style}
    />
  );
}

// ── CategoryPreview ───────────────────────────────────────────────────────────
// Renders a read-only client-view of all sections and their fields.

interface SectionPreviewProps {
  section: GenericSection;
}

function SectionPreview({ section }: SectionPreviewProps) {
  const { data: fields = [], isLoading } = useQuery<GenericField[]>({
    queryKey: ["/api/admin/field-config/sections", section.id, "fields"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/field-config/sections/${section.id}/fields`);
      if (!res.ok) throw new Error("Failed to load fields");
      return res.json();
    },
  });

  const sortedFields = [...fields].filter(f => f.isActive !== false).sort((a, b) => a.sortOrder - b.sortOrder);
  const isTable = section.layoutType === "table";

  return (
    <Card className="overflow-hidden">
      <CardHeader className="py-3 px-4 bg-muted/40 border-b flex-row items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">{section.name}</span>
          <Badge variant={isTable ? "default" : "secondary"} className="text-xs">
            {isTable ? <><Table2 className="h-3 w-3 mr-1" />Table</> : <><LayoutGrid className="h-3 w-3 mr-1" />Card</>}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">{sortedFields.length} fields</span>
      </CardHeader>

      <CardContent className="p-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" />Loading fields...
          </div>
        ) : sortedFields.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4 italic">No fields configured</p>
        ) : isTable ? (
          // Table layout preview — fields as columns, one empty row shown
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-muted/30 border-b">
                  <th className="text-center py-1 px-1 w-8 text-muted-foreground font-medium text-xs">
                    <Check className="h-3.5 w-3.5 mx-auto" />
                  </th>
                  <th className="text-center py-1 px-1 w-8 text-muted-foreground font-medium text-xs">
                    <Trash2 className="h-3.5 w-3.5 mx-auto opacity-40" />
                  </th>
                  {sortedFields.map(field => (
                    <th
                      key={field.id}
                      className="text-left py-1 px-2 font-medium text-muted-foreground text-xs whitespace-nowrap"
                      style={{ minWidth: field.columnWidth || 120 }}
                    >
                      {field.name}
                      {field.isRequired && <span className="text-destructive ml-0.5">*</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Show one placeholder row */}
                <tr className="border-b">
                  <td className="py-2 px-1 text-center">
                    <div className="h-4 w-4 rounded border border-muted-foreground/30 mx-auto" />
                  </td>
                  <td className="py-2 px-1 text-center">
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground/30 mx-auto" />
                  </td>
                  {sortedFields.map(field => (
                    <td key={field.id} className="py-1 px-2" style={{ minWidth: field.columnWidth || 120 }}>
                      <div className="h-7 rounded border border-input bg-background px-2 flex items-center">
                        <span className="text-xs text-muted-foreground/60 truncate">{field.placeholder || field.name}</span>
                      </div>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td colSpan={sortedFields.length + 2} className="py-2 px-2">
                    <button className="text-xs text-muted-foreground flex items-center gap-1.5 opacity-60">
                      <Plus className="h-3.5 w-3.5" />Add Row
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          // Card layout preview — fields in a responsive grid
          <div className="grid grid-cols-12 gap-x-4 gap-y-4">
            {sortedFields.map(field => {
              const colSpanClass = "col-span-12";
              const typeInfo = FIELD_TYPES.find(t => t.value === field.fieldType);
              const TypeIcon = typeInfo?.icon || Type;

              return (
                <div key={field.id} className={`${colSpanClass} space-y-1.5`}>
                  <div className="flex items-center gap-1.5">
                    <div className="h-4 w-4 rounded border border-muted-foreground/20 shrink-0" />
                    <TypeIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {field.name}
                      {field.isRequired && <span className="text-destructive ml-0.5">*</span>}
                    </span>
                    {field.isCritical && (
                      <Badge variant="destructive" className="text-xs py-0">Critical</Badge>
                    )}
                  </div>
                  {field.fieldType === "multiline" || field.fieldType === "notes" ? (
                    <div className="h-16 rounded-md border border-input bg-background px-3 py-2 flex items-start">
                      <span className="text-xs text-muted-foreground/60">{field.placeholder || "Enter text..."}</span>
                    </div>
                  ) : field.fieldType === "checkbox" ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded border border-input bg-background" />
                      <span className="text-xs text-muted-foreground/60">{field.placeholder || "Yes / No"}</span>
                    </div>
                  ) : (
                    <div className="h-9 rounded-md border border-input bg-background px-3 flex items-center">
                      <span className="text-xs text-muted-foreground/60 truncate">{field.placeholder || field.name}</span>
                    </div>
                  )}
                  {field.instructions && (
                    <p className="text-xs text-blue-500/80 italic">{field.instructions}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── CategoryBuilder (main page) ───────────────────────────────────────────────

interface CategoryBuilderProps {
  slug: string;
}

export default function CategoryBuilder({ slug }: CategoryBuilderProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [deletingSectionOpen, setDeletingSectionOpen] = useState(false);
  const [deletingSection, setDeletingSection] = useState<GenericSection | null>(null);
  const [isEditingCategoryName, setIsEditingCategoryName] = useState(false);
  const [categoryNameInput, setCategoryNameInput] = useState("");
  const [previewMode, setPreviewMode] = useState(false);

  // Queries
  const { data: category, isLoading: catLoading } = useQuery<GenericCategory>({
    queryKey: ["/api/generic-categories/by-slug", slug],
    queryFn: async () => {
      const res = await fetch(`/api/generic-categories/by-slug/${slug}?clientId=null`);
      if (!res.ok) throw new Error("Category not found");
      return res.json();
    },
    enabled: !!slug,
  });

  const { data: sections = [], isLoading: sectionsLoading } = useQuery<GenericSection[]>({
    queryKey: ["/api/generic-categories", category?.id, "sections"],
    enabled: !!category?.id,
  });

  const sortedSections = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);

  // Category rename
  const renameCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("PATCH", `/api/admin/field-config/categories/${category!.id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories/by-slug", slug] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/field-config/categories"] });
      setIsEditingCategoryName(false);
      toast({ title: "Category renamed" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // Add section
  const addSectionFlight = useSingleFlight();

  const addSectionMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/generic-sections", {
        categoryId: category!.id,
        name,
        slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
        layoutType: "card",
        allowDocuments: true,
        sortOrder: sortedSections.length,
        isActive: true,
        isDefaultTemplate: true,
        isCustom: false,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories", category?.id, "sections"] });
      toast({ title: "Section added" });
      setAddSectionOpen(false);
      setNewSectionName("");
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // Delete section
  const deleteSectionMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      await apiRequest("DELETE", `/api/generic-sections/${sectionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories", category?.id, "sections"] });
      toast({ title: "Section deleted" });
      setDeletingSectionOpen(false);
      setDeletingSection(null);
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // Reorder sections
  const reorderSectionsMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; sortOrder: number }>) => {
      await apiRequest("PUT", "/api/generic-sections/reorder", { updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories", category?.id, "sections"] });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sortedSections.findIndex(s => s.id === active.id);
    const newIdx = sortedSections.findIndex(s => s.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      const reordered = arrayMove(sortedSections, oldIdx, newIdx);
      reorderSectionsMutation.mutate(reordered.map((s, i) => ({ id: s.id, sortOrder: i })));
    }
  };

  if (catLoading || sectionsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Category not found: {slug}</p>
        <Button variant="ghost" onClick={() => navigate("/categories")} className="p-0 mt-2 gap-1.5"><ArrowLeft className="h-4 w-4" />Back to Categories</Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Back link */}
      <Button variant="ghost" size="sm" onClick={() => navigate("/categories")} className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        All Categories
      </Button>

      {/* Category header */}
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          {getIcon(category.icon, { size: "h-6 w-6" })}
        </div>
        <div className="flex-1 min-w-0">
          {isEditingCategoryName ? (
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                value={categoryNameInput}
                onChange={(e) => setCategoryNameInput(e.target.value)}
                onBlur={() => {
                  if (categoryNameInput.trim() && categoryNameInput !== category.name) {
                    renameCategoryMutation.mutate(categoryNameInput.trim());
                  } else {
                    setIsEditingCategoryName(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                  if (e.key === "Escape") setIsEditingCategoryName(false);
                }}
                className="text-2xl font-bold h-auto py-1"
              />
              {renameCategoryMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h1 className="text-2xl font-bold">{category.name}</h1>
              <Button aria-label="Rename category"
                size="icon"
                variant="ghost"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => { setCategoryNameInput(category.name); setIsEditingCategoryName(true); }}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs capitalize">{category.categoryType}</Badge>
            <span className="text-sm text-muted-foreground">{sortedSections.length} section{sortedSections.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={() => setPreviewMode(p => !p)}
            className="gap-1.5"
            data-testid="button-toggle-preview"
          >
            {previewMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {previewMode ? "Edit Mode" : "Preview"}
          </Button>
          {!previewMode && (
            <Button onClick={() => setAddSectionOpen(true)} className="gap-1.5" data-testid="button-add-section">
              <Plus className="h-4 w-4" />Add Section
            </Button>
          )}
        </div>
      </div>

      {/* Sections */}
      {sortedSections.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground mb-4">No sections yet.</p>
            <Button onClick={() => setAddSectionOpen(true)} variant="outline" className="gap-1.5">
              <Plus className="h-4 w-4" />Add First Section
            </Button>
          </CardContent>
        </Card>
      ) : previewMode ? (
        <div className="space-y-6">
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-dashed">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Client view preview — showing how this category will appear to clients</p>
          </div>
          {sortedSections.map(section => (
            <SectionPreview key={section.id} section={section} />
          ))}
        </div>
      ) : (
        <DndContext sensors={sectionSensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
          <SortableContext items={sortedSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {sortedSections.map(section => (
                <SortableSectionCard
                  key={section.id}
                  section={section}
                  categorySlug={slug}
                  onDelete={(s) => { setDeletingSection(s); setDeletingSectionOpen(true); }}
                  onDuplicated={() => queryClient.invalidateQueries({ queryKey: ["/api/generic-categories", category?.id, "sections"] })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Section dialog */}
      <Dialog open={addSectionOpen} onOpenChange={setAddSectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
            <DialogDescription>Create a new section in "{category.name}"</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="section-name">Section Name</Label>
              <Input
                id="section-name"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newSectionName.trim()) addSectionFlight.runQuiet(() => addSectionMutation.mutateAsync(newSectionName.trim())); }}
                placeholder="e.g., Contact Information"
                autoFocus
                data-testid="input-section-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSectionOpen(false)}>Cancel</Button>
            <Button
              onClick={() => addSectionFlight.runQuiet(() => addSectionMutation.mutateAsync(newSectionName.trim()))}
              disabled={!newSectionName.trim() || addSectionMutation.isPending}
              data-testid="button-submit-section"
            >
              {addSectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Section dialog */}
      <AlertDialog open={deletingSectionOpen} onOpenChange={setDeletingSectionOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Section</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deletingSection?.name}"? This will permanently remove the section and all its fields and client data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletingSection && deleteSectionMutation.mutate(deletingSection.id)}
              disabled={deleteSectionMutation.isPending}
            >
              {deleteSectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Section
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
